import type { ClipboardEvent } from 'react';
import toast from 'react-hot-toast';

export const MAX_SCREENSHOTS = 5;
const MAX_BYTES = 10 * 1024 * 1024;
export const ACCEPTED_SCREENSHOT_TYPES = ['image/png', 'image/jpeg', 'image/webp', 'image/gif'];

/** Keeps the allowed files and explains the rest. */
export function acceptScreenshots(
  current: File[],
  incoming: File[],
  max = MAX_SCREENSHOTS,
  noun = 'screenshots',
): File[] {
  const next = [...current];
  for (const file of incoming) {
    if (!ACCEPTED_SCREENSHOT_TYPES.includes(file.type)) {
      toast.error(`${file.name || 'That file'} isn't a PNG, JPG, WebP, or GIF image.`);
      continue;
    }
    if (file.size > MAX_BYTES) {
      toast.error(`${file.name || 'That image'} is over 10 MB.`);
      continue;
    }
    if (next.length >= max) {
      toast.error(`You can attach up to ${max} ${noun}.`);
      break;
    }
    next.push(file);
  }
  return next;
}

/** onPaste handler for a text box: pasted images become attachments. */
export function pasteScreenshots(
  event: ClipboardEvent<HTMLElement>,
  files: File[],
  onChange: (files: File[]) => void,
  max = MAX_SCREENSHOTS,
  noun = 'screenshots',
) {
  const pasted = Array.from(event.clipboardData.files || []).filter((file) => file.type.startsWith('image/'));
  if (pasted.length === 0) return;
  event.preventDefault();
  const named = pasted.map((file, index) => (
    file.name && file.name !== 'image.png'
      ? file
      : new File([file], `pasted-${Date.now()}-${index + 1}.png`, { type: file.type })
  ));
  onChange(acceptScreenshots(files, named, max, noun));
}
